import setDataToGib from "@casino/legacy/client/components/Auth/Shared/utils/setDataToGib";
import useAuth0Authenticate from "@casino/legacy/client/features/SocialAuth/hooks/useAuth0Authenticate";
import useSocialAuthConf from "@casino/legacy/client/features/SocialAuth/hooks/useSocialAuthConf";
import useSite from "@casino/legacy/client/hooks/useSite";
import useSiteLogo from "@casino/legacy/client/hooks/useSiteLogo";
import { useCallback, useLayoutEffect, useRef } from "react";
import verifyCsrf from "../../csrf/verifyCsrf";
import base64DecodeStateParam from "../helpers/base64DecodeStateParam";
import getQueryItem from "../helpers/getQueryItem";
import useHandleCallbackAccessDenied from "../helpers/handleCallbackAccessDenied";
import useHandleLogin from "../useHandleLogin";
import useHandleRegister from "../useHandleRegister";

const useAuth0Callback = () => {
  const parsed = useRef(false);
  const { loading, auth0Config } = useSocialAuthConf();
  const { siteConfig } = useSite();
  const logo = useSiteLogo();
  const { handleAuth } = useAuth0Authenticate();
  const handleLogin = useHandleLogin();
  const handleRegister = useHandleRegister();
  const { handleCallbackAccessDenied } = useHandleCallbackAccessDenied();

  const handleAuthResult = useCallback(async () => {
    if (parsed.current || loading || !auth0Config) {
      return;
    }
    const error = getQueryItem("error", location.hash);
    if (error) {
      handleCallbackAccessDenied(error, siteConfig);
      return;
    }
    const accessToken = getQueryItem("access_token", location.hash);
    const state = base64DecodeStateParam();
    if (!accessToken || !state) {
      throw new Error("Cognito accessToken or state missing");
    }
    verifyCsrf(state);

    parsed.current = true;

    const result = await handleAuth(accessToken);
    const {
      errors,
      deviceDataToken = "",
      token,
      userManagement,
      sessionId,
    } = result.data?.userManagementAuth0Authenticate ?? {};

    const { success, loginError } = await handleLogin({
      deviceDataToken,
      errors,
      state,
      token,
      userId: userManagement?.profile?._id,
    });

    if (success && userManagement?.profile?._id && sessionId) {
      setDataToGib(userManagement?.profile?._id, sessionId);
    }

    if (loginError || success) {
      return;
    }
    const type = "auth0";
    handleRegister({ accessToken, type, state, errors });
  }, [
    auth0Config,
    handleAuth,
    handleLogin,
    handleRegister,
    loading,
    handleCallbackAccessDenied,
    siteConfig,
  ]);

  useLayoutEffect(() => {
    handleAuthResult();
  });

  return { logo };
};

export default useAuth0Callback;
